<template>
  <div class="h-kpi">
    <div class="h-kpi__label">{{ label }}</div>
    <div class="h-kpi__value">{{ displayValue }}</div>
    <div class="h-kpi__foot" v-if="hasDelta || caption">
      <span v-if="hasDelta" class="h-delta" :class="deltaGood ? 'h-delta--up' : 'h-delta--down'">
        <span class="h-delta__arrow" aria-hidden="true">{{ deltaArrow }}</span>{{ deltaTextResolved }}
      </span>
      <span v-if="caption" class="h-kpi__caption">{{ caption }}</span>
    </div>

    <svg
      v-if="sparklinePoints.length > 1"
      class="h-kpi__spark"
      viewBox="0 0 78 30"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <polyline
        :points="sparkPolyline"
        fill="none"
        :stroke="sparkStroke"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <circle :cx="sparkLast.x" :cy="sparkLast.y" r="2.5" :fill="sparkStroke" />
    </svg>
  </div>
</template>

<script>
// KPI stat tile — label, an auto-compacted value, an optional signed delta
// with an up/down arrow (green/red per whether the move is good), and an
// optional inline SVG sparkline. Matches design/components/cards-kpi.html.
export default {
  name: 'HermesStatTile',
  props: {
    label: { type: String, required: true },
    // Raw number (auto-compacted) or a pre-formatted string (shown verbatim).
    value: { type: [Number, String], default: 0 },
    // If set, the compacted number gets this currency symbol prefixed.
    currency: { type: String, default: '' },
    // Skip compaction, show the number grouped in full.
    raw: { type: Boolean, default: false },
    // Signed change. Sign drives the arrow direction.
    delta: { type: Number, default: null },
    // Pre-formatted delta text (e.g. '6.4%', '0.4pp', '11'); overrides the
    // numeric formatting of `delta` but the sign of `delta` still drives colour/arrow.
    deltaText: { type: String, default: '' },
    // Which direction is "good" (green). 'up' for revenue, 'down' for churn/failures.
    goodDirection: { type: String, default: 'up' },
    // Trailing caption, e.g. 'vs last month'.
    caption: { type: String, default: '' },
    // Array of numbers for the sparkline (chronological).
    sparkline: { type: Array, default: () => [] },
    // Sparkline stroke; defaults to the Sky viz colour. Pass a danger colour for
    // "bad-trend" tiles.
    sparklineColor: { type: String, default: 'var(--viz-1)' },
  },
  computed: {
    displayValue() {
      if (typeof this.value === 'string') return this.value;
      return this.compact(this.value);
    },
    hasDelta() {
      return this.delta !== null && this.delta !== undefined || !!this.deltaText;
    },
    deltaSign() {
      // Prefer explicit numeric delta; else infer from a leading -/+ in deltaText.
      if (this.delta !== null && this.delta !== undefined) return this.delta >= 0 ? 1 : -1;
      return /^-/.test((this.deltaText || '').trim()) ? -1 : 1;
    },
    deltaArrow() {
      return this.deltaSign >= 0 ? '▲' : '▼';
    },
    deltaGood() {
      const up = this.deltaSign >= 0;
      return (up && this.goodDirection === 'up') || (!up && this.goodDirection === 'down');
    },
    deltaTextResolved() {
      if (this.deltaText) return this.deltaText.replace(/^[-+]/, '');
      const n = Math.abs(this.delta);
      return this.compact(n);
    },
    sparklinePoints() {
      return (this.sparkline || []).filter((v) => typeof v === 'number' && !Number.isNaN(v));
    },
    sparkGeometry() {
      const pts = this.sparklinePoints;
      const n = pts.length;
      if (n < 2) return [];
      const min = Math.min(...pts);
      const max = Math.max(...pts);
      const span = max - min || 1;
      const W = 78;
      const H = 30;
      const pad = 4;
      return pts.map((v, i) => ({
        x: (i / (n - 1)) * W,
        // higher value -> smaller y (top); pad so the dot isn't clipped.
        y: pad + (1 - (v - min) / span) * (H - pad * 2),
      }));
    },
    sparkPolyline() {
      return this.sparkGeometry.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
    },
    sparkLast() {
      const g = this.sparkGeometry;
      return g.length ? g[g.length - 1] : { x: 0, y: 0 };
    },
    sparkStroke() {
      return this.sparklineColor;
    },
  },
  methods: {
    // 1284 -> 1.3K ; 4,200,000 -> 4.2M ; <1000 grouped. Prefix currency symbol.
    compact(n) {
      if (n === null || n === undefined || Number.isNaN(Number(n))) return '—';
      const num = Number(n);
      const sym = this.currency ? this.currencySymbol(this.currency) : '';
      const abs = Math.abs(num);
      const sign = num < 0 ? '-' : '';
      if (this.raw || abs < 1000) {
        const grouped = new Intl.NumberFormat(undefined, {
          maximumFractionDigits: this.currency ? 2 : 0,
        }).format(abs);
        return `${sign}${sym}${grouped}`;
      }
      let out;
      if (abs < 1e6) out = `${(abs / 1e3).toFixed(1)}K`;
      else if (abs < 1e9) out = `${(abs / 1e6).toFixed(1)}M`;
      else out = `${(abs / 1e9).toFixed(1)}B`;
      // Strip a trailing .0 (12.0K -> 12K)
      out = out.replace(/\.0([KMB])$/, '$1');
      return `${sign}${sym}${out}`;
    },
    currencySymbol(code) {
      try {
        const parts = new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: code,
          maximumFractionDigits: 0,
        }).formatToParts(0);
        const cur = parts.find((p) => p.type === 'currency');
        return cur ? cur.value : '';
      } catch (e) {
        return '';
      }
    },
  },
};
</script>

<style scoped>
.h-kpi {
  position: relative;
  background: var(--h-bg-card, #fff);
  border: 1px solid var(--h-border, #E2E8F0);
  border-radius: var(--h-radius-lg, 10px);
  padding: 18px 20px;
  box-shadow: var(--h-shadow-card);
  font-family: var(--h-font-body, system-ui);
}
.h-kpi__label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--h-text-secondary, #64748B);
}
.h-kpi__value {
  font-size: 30px;
  font-weight: 600;
  letter-spacing: -0.02em;
  margin-top: 8px;
  line-height: 1;
  color: var(--h-text-primary, #0F172A);
}
.h-kpi__foot {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  font-size: 12.5px;
  color: var(--h-text-secondary, #64748B);
}
.h-delta { display: inline-flex; align-items: center; gap: 3px; font-weight: 600; font-size: 12.5px; }
.h-delta--up { color: var(--h-success-text, #047857); }
.h-delta--down { color: var(--h-danger-text, #B91C1C); }
.h-delta__arrow { font-size: 10px; }
.h-kpi__spark { position: absolute; right: 16px; bottom: 14px; width: 78px; height: 30px; }
</style>
